import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAppStore } from "../store/appStore";
import DataTable, { Column } from "../components/DataTable";
import Modal from "../components/Modal";
import { IconLoader } from "../components/Icons";

interface Operator {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
  last_activity_at: string | null;
}

export default function Operators() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useAppStore((s) => s.user);
  const isSuperadmin = user?.role === "superadmin";

  const { data: operators, isLoading, error } = useQuery({
    queryKey: ["ops-operators"],
    queryFn: async () => {
      const res = await api.get<Operator[]>("/ops/operators?limit=500");
      return res.data;
    },
    enabled: isSuperadmin,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [newForm, setNewForm] = useState({ email: "", full_name: "", is_active: true });
  const [edit, setEdit] = useState<Operator | null>(null);
  const [editForm, setEditForm] = useState<Partial<Operator>>({});
  const [notice, setNotice] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      await api.post("/ops/operators", newForm);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops-operators"] });
      setCreateOpen(false);
      setNewForm({ email: "", full_name: "", is_active: true });
      setNotice(t("operators.inviteSent"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!edit) throw new Error("No operator");
      await api.put(`/ops/operators/${edit.id}`, editForm);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops-operators"] });
      setEdit(null);
    },
  });

  const resetMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/ops/operators/${id}/reset-password`);
    },
    onSuccess: () => setNotice(t("operators.resetSent")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/ops/operators/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ops-operators"] }),
  });

  const columns: Column<Operator>[] = [
    { key: "email", header: t("operators.email"), render: (r) => r.email },
    { key: "fullName", header: t("operators.fullName"), render: (r) => r.full_name ?? "—" },
    {
      key: "active",
      header: t("operators.active"),
      render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? t("common.yes") : t("common.no")}</span>,
    },
    { key: "lastLogin", header: "Poslední přihlášení", render: (r) => r.last_login_at ? new Date(r.last_login_at).toLocaleString("cs-CZ") : "—" },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { setEdit(r); setEditForm({ email: r.email, full_name: r.full_name, is_active: r.is_active }); }} className="ghost" style={{ padding: "6px 10px" }}>{t("common.edit")}</button>
          <button onClick={() => resetMutation.mutate(r.id)} className="ghost" style={{ padding: "6px 10px" }} disabled={resetMutation.isPending}>{t("operators.resetPassword")}</button>
          <button onClick={() => { if (window.confirm(t("common.confirmDelete"))) deleteMutation.mutate(r.id); }} className="danger" style={{ padding: "6px 10px" }} disabled={deleteMutation.isPending}>{t("common.delete")}</button>
        </div>
      ),
    },
  ];

  if (!isSuperadmin) {
    return (
      <div className="card" style={{ padding: 32, color: "var(--muted)" }}>
        {t("operators.superadminOnly")}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h1>{t("nav.operators")}</h1>
          <p>{t("operators.subtitle")}</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="primary">{t("operators.create")}</button>
      </div>

      {notice && (
        <div style={{ padding: 12, marginBottom: 16, background: "var(--green-dim)", color: "var(--green)", borderRadius: "var(--radius-sm)" }}>
          {notice}
        </div>
      )}

      <div className="card">
        <DataTable
          rows={operators ?? []}
          columns={columns}
          loading={isLoading}
          error={error ? t("operators.loadError") : null}
          emptyMessage={t("operators.noOperators")}
        />
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t("operators.create")}>
        <form
          onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <div>
            <label>{t("operators.email")}</label>
            <input type="email" value={newForm.email} onChange={(e) => setNewForm((f) => ({ ...f, email: e.target.value }))} required />
          </div>
          <div>
            <label>{t("operators.fullName")}</label>
            <input value={newForm.full_name} onChange={(e) => setNewForm((f) => ({ ...f, full_name: e.target.value }))} />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={newForm.is_active} onChange={(e) => setNewForm((f) => ({ ...f, is_active: e.target.checked }))} />
            {t("operators.active")}
          </label>
          {createMutation.isError && <div style={{ color: "var(--red)" }}>{t("operators.createError")}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button type="button" onClick={() => setCreateOpen(false)} className="ghost">{t("common.cancel")}</button>
            <button type="submit" className="primary" disabled={createMutation.isPending}>
              {createMutation.isPending ? <IconLoader size={16} /> : t("common.create")}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={t("common.edit")}>
        {edit && (
          <form
            onSubmit={(e) => { e.preventDefault(); updateMutation.mutate(); }}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            <div>
              <label>{t("operators.email")}</label>
              <input type="email" value={editForm.email ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <label>{t("operators.fullName")}</label>
              <input value={editForm.full_name ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={editForm.is_active ?? false} onChange={(e) => setEditForm((f) => ({ ...f, is_active: e.target.checked }))} />
              {t("operators.active")}
            </label>
            {updateMutation.isError && <div style={{ color: "var(--red)" }}>{t("operators.saveError")}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button type="button" onClick={() => setEdit(null)} className="ghost">{t("common.cancel")}</button>
              <button type="submit" className="primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <IconLoader size={16} /> : t("common.save")}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
